import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle, Truck, Package, Eye, Loader2, RefreshCw, AlertCircle, DollarSign, Search } from 'lucide-react';
import { Order, OrderStatus } from '../../types';
import { getOrders } from '../../services/orderService';
import OrderDetailModal from './OrderDetailModal';

interface OrdersTabProps {
  onUpdate: () => void;
}

const OrdersTab: React.FC<OrdersTabProps> = ({ onUpdate }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | OrderStatus>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await getOrders();
      setOrders(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load orders');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOrderUpdate = () => {
    loadOrders();
    onUpdate();
  };

  const filteredOrders = orders.filter(o => {
    const matchesSearch = searchTerm === '' ||
      o.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.retailerName.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || o.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: OrderStatus) => {
    const badges: Record<OrderStatus, { icon: React.ReactNode; classes: string }> = {
      pending: {
        icon: <Clock className="w-3 h-3" strokeWidth={1.5} />,
        classes: 'bg-brass/10 text-brass border border-brass/30'
      },
      payment_received: {
        icon: <DollarSign className="w-3 h-3" strokeWidth={1.5} />,
        classes: 'bg-olive/10 text-olive border border-olive/30'
      },
      processing: {
        icon: <Package className="w-3 h-3" strokeWidth={1.5} />,
        classes: 'bg-steel/10 text-ink-700 border border-steel/30'
      },
      shipped: {
        icon: <Truck className="w-3 h-3" strokeWidth={1.5} />,
        classes: 'bg-ember/5 text-ember-deep border border-ember/30'
      },
      delivered: {
        icon: <CheckCircle className="w-3 h-3" strokeWidth={1.5} />,
        classes: 'bg-olive/15 text-olive border border-olive/40'
      }
    };

    const badge = badges[status];
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 font-mono text-[10px] tracking-micro uppercase ${badge.classes}`}>
        {badge.icon}
        {status.replace('_', ' ')}
      </span>
    );
  };

  const getPaymentBadge = (paymentStatus: string) => {
    if (paymentStatus === 'received') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 font-mono text-[10px] tracking-micro uppercase bg-olive/15 text-olive border border-olive/40">
          <CheckCircle className="w-3 h-3" strokeWidth={1.5} />
          Paid
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 font-mono text-[10px] tracking-micro uppercase bg-brass/10 text-brass border border-brass/30">
        <Clock className="w-3 h-3" strokeWidth={1.5} />
        Awaiting
      </span>
    );
  };

  const getTypeBadge = (orderType?: string) => {
    const isRetail = orderType === 'retail';
    return (
      <span className={`inline-flex w-fit items-center px-2 py-0.5 font-mono text-[10px] tracking-micro uppercase border ${
        isRetail
          ? 'bg-ember/5 text-ember-deep border-ember/30'
          : 'bg-steel/10 text-ink-700 border-steel/30'
      }`}>
        {isRetail ? 'Retail' : 'Wholesale'}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-ember" strokeWidth={1.5} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-ember-soft/25 border-l-2 border-ember p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-ember flex-shrink-0 mt-0.5" strokeWidth={1.5} />
        <div>
          <p className="text-ember-deep font-medium text-sm">Error loading orders</p>
          <p className="text-ember text-sm">{error}</p>
          <button onClick={loadOrders} className="mt-2 text-ember-deep underline text-sm hover:text-ember transition-colors duration-150">
            Try again
          </button>
        </div>
      </div>
    );
  }

  const statusCounts: Record<string, number> = {
    all: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    payment_received: orders.filter(o => o.status === 'payment_received').length,
    processing: orders.filter(o => o.status === 'processing').length,
    shipped: orders.filter(o => o.status === 'shipped').length,
    delivered: orders.filter(o => o.status === 'delivered').length
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-8 gap-4">
        <div>
          <p className="font-mono text-[11px] tracking-micro uppercase text-ember mb-3">— Orders</p>
          <h2 className="font-editorial italic font-light text-3xl text-ink-950 tracking-tight">
            Customer orders.
          </h2>
          <p className="text-ink-700 text-sm mt-1">View and manage customer orders.</p>
        </div>
        <button
          onClick={loadOrders}
          className="inline-flex items-center gap-2 px-4 py-2 text-ink-700 hover:text-ink-950 hover:bg-paper-bright border border-ink-200 hover:border-ink-300 text-sm font-medium transition-colors duration-150"
        >
          <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
          Refresh
        </button>
      </div>

      {/* Search */}
      <div className="flex gap-4 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" strokeWidth={1.5} />
          <input
            type="text"
            placeholder="Search by order number or retailer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-paper-bright border border-ink-200 text-sm text-ink-900 placeholder:text-ink-500 focus:border-ember focus:outline-none focus:ring-1 focus:ring-ember/20 transition-colors duration-150"
          />
        </div>
      </div>

      {/* Status Filters */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {(['all', 'pending', 'payment_received', 'processing', 'shipped', 'delivered'] as const).map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-3 py-1.5 text-sm font-medium transition-colors duration-150 border ${
              statusFilter === status
                ? 'bg-ember text-paper-warm border-ember'
                : 'bg-paper-bright text-ink-700 border-ink-200 hover:border-ink-300 hover:bg-paper'
            }`}
          >
            {status === 'all' ? 'All' : status.replace('_', ' ')}
            <span className="ml-1.5 font-mono text-xs opacity-75">({statusCounts[status]})</span>
          </button>
        ))}
      </div>

      {/* Table */}
      {filteredOrders.length === 0 ? (
        <div className="bg-paper-bright border border-ink-200 p-8 text-center">
          <p className="text-ink-500 text-sm">No orders found.</p>
        </div>
      ) : (
        <div className="bg-paper-bright border border-ink-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-paper border-b border-ink-200">
              <tr>
                <th className="px-4 py-3 text-left font-mono text-[10px] tracking-micro uppercase text-ink-500">Order #</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] tracking-micro uppercase text-ink-500">Retailer</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] tracking-micro uppercase text-ink-500">Date</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] tracking-micro uppercase text-ink-500">Total</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] tracking-micro uppercase text-ink-500">Payment</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] tracking-micro uppercase text-ink-500">Status</th>
                <th className="px-4 py-3 text-right font-mono text-[10px] tracking-micro uppercase text-ink-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-200">
              {filteredOrders.map((order) => (
                <tr key={order.id} className="hover:bg-paper transition-colors duration-150">
                  <td className="px-4 py-4 font-mono text-sm text-ink-950">
                    {order.orderNumber}
                  </td>
                  <td className="px-4 py-4 text-ink-900 text-sm">
                    <div className="flex flex-col gap-1.5">
                      <span>{order.retailerName}</span>
                      {getTypeBadge(order.orderType)}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-ink-700 text-sm">
                    {order.createdAt.toLocaleDateString()}
                  </td>
                  <td className="px-4 py-4 font-medium text-ink-950 text-sm">
                    ${order.total.toFixed(2)}
                  </td>
                  <td className="px-4 py-4">
                    {getPaymentBadge(order.paymentStatus)}
                  </td>
                  <td className="px-4 py-4">
                    {getStatusBadge(order.status)}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <button
                      onClick={() => setSelectedOrder(order)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-ember hover:text-ember-deep hover:bg-ember-soft/25 transition-colors duration-150"
                    >
                      <Eye className="w-4 h-4" strokeWidth={1.5} />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {selectedOrder && (
        <OrderDetailModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onUpdate={handleOrderUpdate}
        />
      )}
    </div>
  );
};

export default OrdersTab;
